import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";

function formatDateTimeLocalFromDate(d) {
  if (!d || Number.isNaN(d.getTime())) return "";
  const pad = (n) => n.toString().padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function formatDateTimeLocal(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  return formatDateTimeLocalFromDate(d);
}

function parseDateTimeLocal(value) {
  if (!value) return null;
  const [datePart, timePart] = value.split("T");
  if (!datePart) return null;
  const [y, m, d] = datePart.split("-").map((x) => parseInt(x, 10));
  const [hh, mm] = (timePart || "00:00").split(":").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0);
}

function addDaysToDateTimeLocal(currentValue, daysToAdd) {
  const days = parseInt(daysToAdd, 10);
  if (!Number.isFinite(days) || days === 0) return currentValue;
  const base = parseDateTimeLocal(currentValue) || new Date();
  base.setDate(base.getDate() + days);
  return formatDateTimeLocalFromDate(base);
}

function SuperAdmin() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [deviceSessions, setDeviceSessions] = useState([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    logo: "",
    contactNumber: "",
    offlinePassKey: "",
    onlinePassKey: "",
    offlinePassKeyExpiresAt: "",
    onlinePassKeyExpiresAt: ""
  });
  const [editFormData, setEditFormData] = useState({
    username: "",
    email: "",
    password: "",
    logo: "",
    contactNumber: "",
    shopName: "",
    address: "",
    offlinePassKey: "",
    onlinePassKey: "",
    offlinePassKeyExpiresAt: "",
    onlinePassKeyExpiresAt: ""
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Expiry extension helpers (separate from payload)
  const [addOfflineExtendDays, setAddOfflineExtendDays] = useState("");
  const [addOnlineExtendDays, setAddOnlineExtendDays] = useState("");
  const [editOfflineExtendDays, setEditOfflineExtendDays] = useState("");
  const [editOnlineExtendDays, setEditOnlineExtendDays] = useState("");

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem("adminToken");
  };

  // Check authentication on mount
  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/admin/login");
    }
    const storedAdmin = localStorage.getItem("adminData");
    if (storedAdmin) {
      try {
        setAdminInfo(JSON.parse(storedAdmin));
      } catch {
        setAdminInfo(null);
      }
    }
  }, [navigate]);

  // Fetch all companies
  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      const response = await api.get("/admin/companies", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error("Fetch companies error:", error);
      toast.error(error.response?.data?.message || "Failed to fetch companies");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Handle form change
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Add new company
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const token = getToken();
      const response = await api.post("/admin/companies", formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      toast.success("Company added successfully!");
      setShowAddModal(false);
      setFormData({ username: "", email: "", password: "", logo: "", contactNumber: "", offlinePassKey: "", onlinePassKey: "", offlinePassKeyExpiresAt: "", onlinePassKeyExpiresAt: "" });
      setAddOfflineExtendDays("");
      setAddOnlineExtendDays("");
      fetchCompanies();
    } catch (error) {
      console.error("Add company error:", error);
      toast.error(error.response?.data?.message || "Failed to add company");
    } finally {
      setIsLoading(false);
    }
  };

  // Delete company
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this company?")) {
      return;
    }

    try {
      const token = getToken();
      await api.delete(`/admin/companies/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      toast.success("Company deleted successfully!");
      fetchCompanies();
    } catch (error) {
      console.error("Delete company error:", error);
      toast.error(error.response?.data?.message || "Failed to delete company");
    }
  };

  // Toggle company active status
  const handleToggleActive = async (id, currentStatus) => {
    try {
      const token = getToken();
      await api.put(
        `/admin/companies/${id}`,
        { isActive: !currentStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      toast.success("Company status updated!");
      fetchCompanies();
    } catch (error) {
      console.error("Toggle active error:", error);
      toast.error(error.response?.data?.message || "Failed to update company");
    }
  };

  // Handle edit button click
  const handleEditClick = async (id) => {
    try {
      setIsLoading(true);
      const token = getToken();
      const response = await api.get(`/admin/companies/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const company = response.data.company;
      setEditingCompany(company);
      setEditFormData({
        username: company.username || "",
        email: company.email || "",
        password: "",
        logo: company.logo || "",
        contactNumber: company.contactNumber || "",
        shopName: company.shopName || "",
        address: company.address || "",
        offlinePassKey: company.offlinePassKey || "",
        onlinePassKey: company.onlinePassKey || "",
        offlinePassKeyExpiresAt: formatDateTimeLocal(company.offlinePassKeyExpiresAt),
        onlinePassKeyExpiresAt: formatDateTimeLocal(company.onlinePassKeyExpiresAt)
      });
      setEditOfflineExtendDays("");
      setEditOnlineExtendDays("");
      setShowEditModal(true);
    } catch (error) {
      console.error("Fetch company error:", error);
      toast.error(error.response?.data?.message || "Failed to fetch company");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit form change
  const handleEditChange = (e) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value
    });
  };

  // Update company
  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const token = getToken();
      const updateData = { ...editFormData };
      // Only include password if it's been changed
      if (!updateData.password) {
        delete updateData.password;
      }
      // Dates empty string -> undefined so backend keeps/clears correctly
      if (!updateData.offlinePassKeyExpiresAt) {
        updateData.offlinePassKeyExpiresAt = "";
      }
      if (!updateData.onlinePassKeyExpiresAt) {
        updateData.onlinePassKeyExpiresAt = "";
      }
      await api.put(`/admin/companies/${editingCompany._id}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      toast.success("Company updated successfully!");
      setShowEditModal(false);
      setEditingCompany(null);
      setEditFormData({ username: "", email: "", password: "", logo: "", contactNumber: "", shopName: "", address: "", offlinePassKey: "", onlinePassKey: "", offlinePassKeyExpiresAt: "", onlinePassKeyExpiresAt: "" });
      fetchCompanies();
    } catch (error) {
      console.error("Update company error:", error);
      toast.error(error.response?.data?.message || "Failed to update company");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    toast.info("Logged out from Super Admin panel");
    navigate("/admin/login");
  };

  return (
    <>
      <Helmet><title>Super Admin Panel - Mandi Khata</title></Helmet>
      
      <div className="min-h-screen bg-brand-background">
        {/* Mobile Menu Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        <div className="flex flex-col md:flex-row">
          {/* Sidebar - Fixed */}
          <aside className={`fixed top-0 left-0 h-screen w-64 bg-white shadow-md border-r border-brand-card/60 z-40 overflow-y-auto transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
          }`}>
            <div className="p-4 border-b border-brand-card/40 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-brand-header">Admin Navigation</h2>
                <p className="text-xs text-gray-500 mt-1">Quick access to tools</p>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="md:hidden text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="p-3 space-y-2 text-sm">
              <button
                onClick={() => {
                  navigate('/admin');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Dashboard</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/bills');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Bill Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/fields');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Field Settings</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/roznamacha-fields');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Roz Namacha Fields</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/recoveries');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Recovery Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/expenses');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Expense Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/payment-methods');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Payment Methods</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/team-members');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Team Members</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/recognitions');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Recognitions</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/blogs');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Blogs</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/bill-header-image');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Bill Header Manage</span>
                </div>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/company-dashboard-management');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span>Company Dashboard Management</span>
                </div>
              </button>
              <button
                onClick={() => {
                  navigate('/company/dashboard');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Company Dashboard</span>
              </button>
              <button
                onClick={() => {
                  handleAdminLogout();
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Logout</span>
              </button>
            </nav>
          </aside>

          {/* Main Content */}
          <div className="flex-1 md:ml-64 p-4 md:p-8">
          {/* Mobile Header with Menu Button */}
          <div className="md:hidden mb-4 flex items-center justify-between bg-white rounded-lg shadow-md p-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-600 hover:text-gray-900"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Super Admin</h1>
            <div className="w-6"></div>
          </div>

          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6 border border-brand-card/60">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-brand-header flex items-center justify-center text-brand-button font-bold">
                  MK
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-brand-header">Super Admin Panel</h1>
                  <p className="text-gray-600 mt-1 text-sm md:text-base">Manage all companies for Mandi Khata</p>
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                {adminInfo && (
                  <div className="text-right text-sm">
                    <p className="font-semibold text-gray-800">{adminInfo.name || adminInfo.username || "Admin"}</p>
                    <p className="text-gray-500">{adminInfo.email}</p>
                  </div>
                )}
                <div className="flex gap-2 md:gap-4">
                  <button
                    onClick={() => navigate('/admin/bills')}
                    className="bg-brand-header hover:bg-emerald-800 text-brand-background font-semibold px-4 md:px-6 py-2 md:py-3 rounded-lg transition transform hover:-translate-y-0.5 hover:shadow-lg text-sm md:text-base"
                  >
                    Bill Management
                  </button>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="bg-brand-button hover:bg-yellow-300 text-gray-900 font-semibold px-4 md:px-6 py-2 md:py-3 rounded-lg transition transform hover:-translate-y-0.5 hover:shadow-lg text-sm md:text-base"
                  >
                    + Add Company
                  </button>
                  <button
                    onClick={handleAdminLogout}
                    className="border border-brand-header text-brand-header hover:bg-brand-header hover:text-brand-background font-semibold px-4 md:px-5 py-2 rounded-lg transition text-sm md:text-base"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-3 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-brand-card/60 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Companies</p>
              <p className="mt-2 text-2xl font-bold text-brand-header">{companies.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-brand-card/60 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Active</p>
              <p className="mt-2 text-2xl font-bold text-emerald-700">
                {companies.filter(c => c.isActive).length}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-brand-card/60 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Inactive</p>
              <p className="mt-2 text-2xl font-bold text-brand-alert">
                {companies.filter(c => !c.isActive).length}
              </p>
            </div>
          </div>

          {/* Companies Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-brand-card/60">
            {isLoading && companies.length === 0 ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading companies...</p>
              </div>
            ) : companies.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-600">No companies found. Add your first company!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-brand-background">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Username
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Shop Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Logo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        رابطہ نمبر
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dashboard
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {companies.map((company) => (
                      <tr key={company._id} className="hover:bg-brand-background/60 transition">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{company.username}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{company.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {company.shopName || <span className="text-gray-400">Not set</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {company.logo ? (
                              <img src={company.logo} alt="Logo" className="h-10 w-10 rounded object-cover" />
                            ) : (
                              <span className="text-gray-400">Not set</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {company.contactNumber || <span className="text-gray-400">Not set</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              company.dashboardType === 'part2' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {company.dashboardType === 'part2' ? 'CompanyDashboard Part 2' : 'Main Dashboard'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleToggleActive(company._id, company.isActive)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              company.isActive
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-red-50 text-brand-alert border border-red-200"
                            }`}
                          >
                            {company.isActive ? "Active" : "Inactive"}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleEditClick(company._id)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={async () => {
                                await fetchCompanies();
                                const token = getToken();
                                try {
                                  setIsLoadingDevices(true);
                                  const res = await api.get(`/admin/companies/${company._id}/devices`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  setDeviceSessions(res.data.sessions || []);
                                  setEditingCompany(company);
                                  setShowDeviceModal(true);
                                } catch (err) {
                                  console.error("Fetch device sessions error:", err);
                                  toast.error("Failed to load device details");
                                } finally {
                                  setIsLoadingDevices(false);
                                }
                              }}
                              className="text-gray-700 hover:text-gray-900"
                            >
                              View Details
                            </button>
                            <button
                              onClick={() => handleDelete(company._id)}
                              className="text-brand-alert hover:text-red-900"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        </div>

        {/* Add Company Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Add New Company</h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setAddOfflineExtendDays("");
                    setAddOnlineExtendDays("");
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username *
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="Enter username"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="company@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="Minimum 6 characters"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo URL
                  </label>
                  <input
                    type="text"
                    name="logo"
                    value={formData.logo}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="https://example.com/logobest.png"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    رابطہ نمبر
                  </label>
                  <input
                    type="tel"
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="+92 300 1234567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Offline Passkey
                  </label>
                  <input
                    type="password"
                    name="offlinePassKey"
                    value={formData.offlinePassKey}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="Optional offline key for desktop app"
                  />
                  <label className="block text-xs text-gray-600 mt-2 mb-1">
                    Offline Passkey Expiry (Date &amp; Time)
                  </label>
                  <input
                    type="datetime-local"
                    name="offlinePassKeyExpiresAt"
                    value={formData.offlinePassKeyExpiresAt}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  />
                  <div className="flex items-end gap-2 mt-2">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 mb-1">
                        Extend offline expiry (days)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={addOfflineExtendDays}
                        onChange={(e) => setAddOfflineExtendDays(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                        placeholder="e.g. 7"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          offlinePassKeyExpiresAt: addDaysToDateTimeLocal(
                            prev.offlinePassKeyExpiresAt,
                            addOfflineExtendDays
                          )
                        }));
                        setAddOfflineExtendDays("");
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                    >
                      Extend
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Online Passkey
                  </label>
                  <input
                    type="password"
                    name="onlinePassKey"
                    value={formData.onlinePassKey}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="Optional online key for desktop app"
                  />
                  <label className="block text-xs text-gray-600 mt-2 mb-1">
                    Online Passkey Expiry (Date &amp; Time)
                  </label>
                  <input
                    type="datetime-local"
                    name="onlinePassKeyExpiresAt"
                    value={formData.onlinePassKeyExpiresAt}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  />
                  <div className="flex items-end gap-2 mt-2">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 mb-1">
                        Extend online expiry (days)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={addOnlineExtendDays}
                        onChange={(e) => setAddOnlineExtendDays(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                        placeholder="e.g. 7"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          onlinePassKeyExpiresAt: addDaysToDateTimeLocal(
                            prev.onlinePassKeyExpiresAt,
                            addOnlineExtendDays
                          )
                        }));
                        setAddOnlineExtendDays("");
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                    >
                      Extend
                    </button>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setAddOfflineExtendDays("");
                      setAddOnlineExtendDays("");
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition disabled:opacity-50"
                  >
                    {isLoading ? "Adding..." : "Add Company"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Company Modal */}
        {showEditModal && editingCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Edit Company</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingCompany(null);
                    setEditFormData({ username: "", email: "", password: "", logo: "", contactNumber: "", shopName: "", address: "", offlinePassKey: "", onlinePassKey: "", offlinePassKeyExpiresAt: "", onlinePassKeyExpiresAt: "" });
                    setEditOfflineExtendDays("");
                    setEditOnlineExtendDays("");
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username *
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={editFormData.username}
                    onChange={handleEditChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="Enter username"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={editFormData.email}
                    onChange={handleEditChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="company@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password (leave blank to keep current)
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={editFormData.password}
                    onChange={handleEditChange}
                    minLength={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="Enter new password (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shop Name
                  </label>
                  <input
                    type="text"
                    name="shopName"
                    value={editFormData.shopName}
                    onChange={handleEditChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="Enter shop name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <textarea
                    name="address"
                    value={editFormData.address}
                    onChange={handleEditChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="Enter address"
                  />
                </div>

                {/* Edit passkeys + expiry */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Offline Passkey
                  </label>
                  <input
                    type="password"
                    name="offlinePassKey"
                    value={editFormData.offlinePassKey}
                    onChange={handleEditChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="Optional offline key for desktop app"
                  />
                  <label className="block text-xs text-gray-600 mt-2 mb-1">
                    Offline Passkey Expiry (Date &amp; Time)
                  </label>
                  <input
                    type="datetime-local"
                    name="offlinePassKeyExpiresAt"
                    value={editFormData.offlinePassKeyExpiresAt}
                    onChange={handleEditChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  />
                  <div className="flex items-end gap-2 mt-2">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 mb-1">
                        Extend offline expiry (days)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={editOfflineExtendDays}
                        onChange={(e) => setEditOfflineExtendDays(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                        placeholder="e.g. 7"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setEditFormData((prev) => ({
                          ...prev,
                          offlinePassKeyExpiresAt: addDaysToDateTimeLocal(
                            prev.offlinePassKeyExpiresAt,
                            editOfflineExtendDays
                          )
                        }));
                        setEditOfflineExtendDays("");
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                    >
                      Extend
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Online Passkey
                  </label>
                  <input
                    type="password"
                    name="onlinePassKey"
                    value={editFormData.onlinePassKey}
                    onChange={handleEditChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="Optional online key for desktop app"
                  />
                  <label className="block text-xs text-gray-600 mt-2 mb-1">
                    Online Passkey Expiry (Date &amp; Time)
                  </label>
                  <input
                    type="datetime-local"
                    name="onlinePassKeyExpiresAt"
                    value={editFormData.onlinePassKeyExpiresAt}
                    onChange={handleEditChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                  />
                  <div className="flex items-end gap-2 mt-2">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 mb-1">
                        Extend online expiry (days)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={editOnlineExtendDays}
                        onChange={(e) => setEditOnlineExtendDays(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                        placeholder="e.g. 7"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setEditFormData((prev) => ({
                          ...prev,
                          onlinePassKeyExpiresAt: addDaysToDateTimeLocal(
                            prev.onlinePassKeyExpiresAt,
                            editOnlineExtendDays
                          )
                        }));
                        setEditOnlineExtendDays("");
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                    >
                      Extend
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo URL
                  </label>
                  <input
                    type="text"
                    name="logo"
                    value={editFormData.logo}
                    onChange={handleEditChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="https://example.com/logobest.png"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    رابطہ نمبر
                  </label>
                  <input
                    type="tel"
                    name="contactNumber"
                    value={editFormData.contactNumber}
                    onChange={handleEditChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="+92 300 1234567"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingCompany(null);
                      setEditFormData({ username: "", email: "", password: "", logo: "", contactNumber: "", shopName: "", address: "", offlinePassKey: "", onlinePassKey: "", offlinePassKeyExpiresAt: "", onlinePassKeyExpiresAt: "" });
                      setEditOfflineExtendDays("");
                      setEditOnlineExtendDays("");
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition disabled:opacity-50"
                  >
                    {isLoading ? "Updating..." : "Update Company"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
        {/* Device Sessions Modal */}
        {showDeviceModal && editingCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Device Sessions – {editingCompany.username}
                </h2>
                <button
                  onClick={() => {
                    setShowDeviceModal(false);
                    setDeviceSessions([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {isLoadingDevices ? (
                <p className="text-sm text-gray-500">Loading device sessions...</p>
              ) : deviceSessions.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No desktop device sessions recorded yet for this company.
                </p>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="px-3 py-2 text-left">When</th>
                        <th className="px-3 py-2 text-left">Mode</th>
                        <th className="px-3 py-2 text-left">IP</th>
                        <th className="px-3 py-2 text-left">OS</th>
                        <th className="px-3 py-2 text-left">App</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deviceSessions.map((s) => (
                        <tr key={s._id} className="border-t border-gray-200">
                          <td className="px-3 py-2">
                            {new Date(s.createdAt).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 capitalize">{s.mode || '-'}</td>
                          <td className="px-3 py-2">{s.ip || '-'}</td>
                          <td className="px-3 py-2">{s.os || '-'}</td>
                          <td className="px-3 py-2">{s.appVersion || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
    </>
  );
}

export default SuperAdmin;

